import {doc, getDoc, setDoc, Timestamp} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {db, functions} from './firebase';
import {AppPlatform, AppVersions} from '../types/feedback';

const APP_VERSIONS_DOC = 'config/app_versions';

export async function getAppVersions(): Promise<AppVersions | null> {
    const docRef = doc(db, APP_VERSIONS_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        return docSnap.data() as AppVersions;
    }

    return null;
}

export async function updateAppVersions(versions: Partial<Omit<AppVersions, 'updatedAt'>>): Promise<void> {
    const docRef = doc(db, APP_VERSIONS_DOC);
    await setDoc(docRef, {
        ...versions,
        updatedAt: Timestamp.now(),
    }, {merge: true});
}

export async function updatePlatformVersion(platform: AppPlatform, version: string): Promise<void> {
    const docRef = doc(db, APP_VERSIONS_DOC);
    await setDoc(docRef, {
        [platform]: version,
        updatedAt: Timestamp.now(),
    }, {merge: true});
}

// Clean version string by removing non-numeric prefixes/suffixes
// e.g., "v4.1.23988 (8)" -> "4.1.23988"
function cleanVersion(version: string): string {
    // Remove leading 'v' or 'V'
    let cleaned = version.replace(/^[vV]/, '');
    // Remove trailing parenthetical content like " (8)"
    cleaned = cleaned.replace(/\s*\(.*\)\s*$/, '');
    return cleaned.trim();
}

// Check if version uses build number format (3rd segment >= 1000)
// Build numbers like 4.1.23988 are older iOS versions before semantic versioning
function hasBuildNumber(parts: number[]): boolean {
    return parts.length >= 3 && parts[2] >= 1000;
}

// Compare two version strings (e.g., "1.2.3" vs "1.2.4")
// Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
// Handles build number versions (e.g., 4.1.23988) which are older than semantic versions (e.g., 4.1.26)
export function compareVersions(v1: string, v2: string): number {
    const cleaned1 = cleanVersion(v1);
    const cleaned2 = cleanVersion(v2);

    const parts1 = cleaned1.split('.').map(Number);
    const parts2 = cleaned2.split('.').map(Number);

    // Compare major.minor first
    for (let i = 0; i < 2; i++) {
        const p1 = parts1[i] || 0;
        const p2 = parts2[i] || 0;

        if (p1 < p2) return -1;
        if (p1 > p2) return 1;
    }

    // If major.minor are equal, handle build numbers specially
    const v1HasBuild = hasBuildNumber(parts1);
    const v2HasBuild = hasBuildNumber(parts2);

    // Build number versions are older than semantic versions
    if (v1HasBuild && !v2HasBuild) return -1;
    if (!v1HasBuild && v2HasBuild) return 1;

    // Both are same type, compare remaining segments normally
    const maxLength = Math.max(parts1.length, parts2.length);
    for (let i = 2; i < maxLength; i++) {
        const p1 = parts1[i] || 0;
        const p2 = parts2[i] || 0;

        if (p1 < p2) return -1;
        if (p1 > p2) return 1;
    }

    return 0;
}

// Check if user version is outdated compared to latest
export function isVersionOutdated(userVersion: string, latestVersion: string): boolean {
    return compareVersions(userVersion, latestVersion) < 0;
}

// Fetch latest versions from app stores via Cloud Function
export async function getStoreVersions(): Promise<Record<string, string>> {
    const fn = httpsCallable<Record<string, never>, Record<string, string>>(functions, 'getStoreVersions');
    const result = await fn({});
    return result.data;
}
